<template>
	<div
		class="input-wrapper"
		@click="click()"
		:class="{
			'active': isActive,
			'has-error': hasError,
			'empty': value ? value.length === 0 : true,
			'has-pretext' : preText
		}"
	>
		<label v-if="label">
			<span v-if="required">*</span> {{ label }}
		</label>

		<span class="pre-text" v-if="preText">
			{{ preText }}
		</span>

		<input
			:value="value"
			v-if="!isTextArea"
			class="input-field"
			ref="inputText"
			:type="toggleType ? toggleType : type"
			:placeholder="placeholder"
			:maxlength="limit"
			:step="step"
			:autocomplete="!autoComplete ? 'new-password' : true"
			@paste="onPaste($event)"
			@blur="blur()"
			@input="update($event)"
			@focus="focus()"
			@keypress="validate($event)"
			:disabled="disable"
		/>

		<textarea
			:value="value"
			:disabled="disable"
			v-if="isTextArea"
			class="textarea"
			ref="inputText"
			:maxlength="limit"
			@blur="blur()"
			@input="update($event)"
			@focus="focus()">
		</textarea>

		<div class="text-area-limit" v-if="isTextArea">
			<span>
				{{ limit - value.length }}
			</span>
		</div>

		<div
			class="toggle-password"
			v-if="togglePassword"
			@click="toggleInput()"
		>
			{{ toggleType=='text' ? 'HIDE' : 'SHOW' }}
		</div>

		<div
			class="helper-text"
			v-if="helperText"
		>
			{{ helperText }}
		</div>
	</div>
</template>
<script>
import { mapGetters } from 'vuex'

export default {
	props: {
		type: {
			type: String,
			default: 'text'
		},

		value: {
			default: ''
		},

		hasError: {
			type: Boolean,
			default: false
		},

		preText: {
			type: String,
			default: ''
		},

		helperText: {
			type: String,
			default: null
		},

		label: {
			type: String,
			default: ''
		},

		placeholder: {
			type: String,
			default: ''
		},

		limit: {
			type: Number,
			default: 255
		},

		togglePassword: {
			type: Boolean,
			default: false
		},

		autoComplete: {
			type: Boolean,
			default: false
		},

		isTextArea: {
			type: Boolean,
			default: false
		},

		numMinimum: {
			type: Number,
			default: 0
		},

		preventPaste: {
			type: Boolean,
			default: false
		},

		required: {
			type: Boolean,
			default: false
		},

		disable: {
			type: Boolean,
			default: false
		},

		step: {
			type: [Number, String],
			default: 1
		}
	},

	data () {
		return {
			isActive: false,
			showPassword: false,
			toggleType: null
		}
	},

	computed: {
		...mapGetters({
			signInText: 'getLoginTranslation'
		})
	},

	methods: {
		focus () {
			this.isActive = true
		},

        click () {
            this.$refs.inputText.focus()
            this.$emit('click')
        },

		blur () {
			this.isActive = false

			if (!this.isTextArea) {
				if (!this.$refs.inputText) return

				this.$refs.inputText.type = this.type
			}

			this.toggleType = null
		},

		toggleInput () {
			this.showPassword = !this.showPassword
			this.toggleType = this.showPassword ? 'text' : 'password'
		},

		validate (e) {
			if (this.disable) {
				e.preventDefault()
				return
			}

			if (this.type === 'number' && (isNaN(e.target.value) || e.target.value.length > this.limit)) {
				e.preventDefault()
			}

			if (this.type === 'number' && (e.target.value === '' || parseInt(e.target.value) === 0) && parseInt(e.key) < this.numMinimum) {
				e.preventDefault()
			}

			if (this.type === 'number' && String(event.target.value).indexOf('.') > -1 && e.key === '.') {
				e.preventDefault()
			}
		},

		onPaste (e) {
			if (this.preventPaste) e.preventDefault()
		},

		update (event) {
			let regEx = this.step === 1 ? /^[0-9]*$/ : /^[0-9]*\.?[0-9]+$/

			if (this.type === 'number' && !regEx.test(event.target.value)) {
				event.target.value = ''
			}

			if (this.type === 'number' && parseInt(event.target.value) < this.numMinimum) {
				event.target.value = ''
			}

			this.$emit('input', event.target.value)
		}
	}
}
</script>
<style lang="scss" scoped>
.input-wrapper {
	cursor: pointer;
	position: relative;
	display: flex;
    flex-direction: column;
	justify-content: flex-end;

	&.has-pretext {
		.input-field {
			padding-left: 55px !important;
		}
	}

	label {
		display: block;
		text-align: left;
		font-weight: 400;
		font-size: 13px;
		transition: 0.3s;

		span {
			color: #BC3211;
		}
	}

	.pre-text {
		position: absolute;
		bottom: 11px;
		z-index: 1;
		left: 10px;
		text-transform: uppercase;
	}

	.input-field, .textarea {
		display: block;
		text-align: left;
		width: 100%;
		outline: none;
		font-size: 16px;
		color: #4A4C50;
		border: none;
		transition: height 0.3s ease;
		transition: 0.3s;

		&:disabled {
			background-color: #fff;
			opacity: 0.7;
			pointer-events: none;
		}
	}

	.text-area-limit {
		margin-top: 10px;
		display: flex;
		justify-content: flex-end;
		color: #575B5C;
	}

	.toggle-password {
		position: absolute;
		color: #979CAC;
		background-color: #fff;
		padding: 5px 10px;
		cursor: pointer;

		&:hover {
			color: darken(#979CAC,20%);
		}
	}

	.helper-text {
		display: block;
		text-align: left;
		font-size: 13px;
		pointer-events: none;
	}

	&.empty {
		.toggle-password {
			display: none;
		}
	}

	// style for line type input field
	&.input-line {
		height: 30px;

		label {
			color: #979CAC;
			pointer-events: none;
		}

		.input-field {
			border-bottom: 2px solid #D8DEEB;
			margin: 10px 0;
			padding-bottom: 10px;

			&::placeholder {
				font-size: 16px;
				line-height: 20px;
				color: #979CAC;
			}
		}

		.toggle-password {
			top: -14px;
			right: 0;
		}

		.helper-text {
			color: #4D4F5C;
		}

		&:hover {
			.input-field {
				border-bottom: 2px solid #979CAC;
			}
		}

		&.empty {
			.input-field {
				height: 0;
				padding: 0;
			}

			label {
				font-size: 16px;
				color: #575B5C;
			}
		}

		&.active{

			label {
				color: #2DC4B6;
				font-size: 13px;
			}

			.input-field {
				border-bottom: 3px solid #2DC4B6;
				padding-bottom: 10px;
				height: 33px;
			}
		}

		&.has-error {
			height: 50px;

			.input-field {
				border-bottom: 3px solid #E71D36;
			}

			label,.helper-text {
				color: #E71D36;
			}
		}
	}

	// style for box type input field
	&.input-box {

		&.textarea {
			width: 100%;

			.textarea {
				height: 100px;
				resize: none;
			}
		}

		label {
			font-size: 16px;
			color: #575B5C;
			pointer-events: none;
			padding: 10px 0;
			width: 100%;
		}

		.input-field, .textarea {
			border: 1px solid #C6C6C6;
			border-radius: 4px;
			font-size: 13px;
			padding: 10px;

			&::placeholder {
				color: #CECFD0;
				font-size: 13px;
			}
		}

		.toggle-password {
			top: 45px;
			right: 5px;
		}

		.helper-text {
			color: #808495;
		}

		&:hover {
			.input-field, .textarea {
				border: 1px solid #2DC4B6;
			}

			label {
				color: #2DC4B6;
			}
		}

		&.active{

			label {
				color: #2DC4B6;
			}

			.input-field, .textarea {
				border: 1px solid #2DC4B6;
			}
		}

		&.has-error {
			.input-field, .textarea {
				border: 1px solid #E71D36;
			}

			label,.helper-text {
				color: #E71D36;
			}
		}
	}
}
</style>
